//Packages
import React, {useEffect} from "react";
import {Helmet} from "react-helmet-async";
import ErrorBoundary from "antd/lib/alert/ErrorBoundary";
//Core
import {Customer, FULL_PORTAL_STAGES, Project} from "@natomas-org/core";
//Styles
import {PortalLayout} from "./styles";
//Shared
import {getPageComponent} from "../_shared/navigation/PageNavigation";
import {useUrl} from "../../_shared/hooks/useUrl";
import {getStore} from "../../../store";
import {updateAdditionLeftPadding} from "../../_shared/slices/PageSlice";
import {LoadingPanel} from "../../_shared/generics/loading-panel";
import {useCurrentUser} from "../../_shared/hooks/useCurrentUser";
import {useCustomerStatus} from "../../_shared/hooks/useCustomerStatus";
import {useCurrentCustomer} from "../../_shared/hooks/useCurrentCustomer";
import {HCPDashboardLayout} from "./HCPDashboardLayout/HCPDashboardLayout";
import {FPDashboardLayout} from "./FPDashboardLayout/FPDashboardLayout";
import {THANK_YOU_PAGE_ID} from "../_shared/navigation/constants";
import useActiveProject from "../../_shared/hooks/useProject/useActiveProject";
import {Header} from "../../../0-app/1-container/header/header";
import B2BView from "./B2BView";  
import PrimaryHomesSummary from "src/0-app/0-page/summary/primary-homes/summary";
import { getProjectService } from "src/api/project/ProjectService";
import { useAuthenticatedQuery } from "../../_shared/hooks/useAuthenticatedQuery";

const getProjectPortalType = async (pid: string) => {
  try {
    const service = await getProjectService();
    const r = await service.getProjectPortalType(pid);
    return r?.data?.portal_type;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const useProjectPortalType = (pid: string) => {
  const {data} = useAuthenticatedQuery({
    queryKey: ["projectPortalType", pid],
    queryFn: () => getProjectPortalType(pid),
    enabled: !!pid, // Only run when there's a valid pid (auth is handled by useAuthenticatedQuery)
  });
  return data;
};

export const ProjectSummaryLayout = () => {
  const {pageId} = useUrl();
  const {isCustomerPreDeposit} = useCustomerStatus();
  const {isAdmin, isSignedIn} = useCurrentUser();
  const {customer} = useCurrentCustomer();
  const {info, id: pid} = useActiveProject();
  const portalType = useProjectPortalType(pid);
  const isDepositPaid = !isCustomerPreDeposit;
  const currentStage = Project.getCurrentStage(info);
  const isThankYouPage = pageId === THANK_YOU_PAGE_ID && isDepositPaid;
  const isStageInFullPortal = currentStage && FULL_PORTAL_STAGES.includes(currentStage);

  useEffect(() => {
    getStore().dispatch(updateAdditionLeftPadding(0));
    return () => {
      getStore().dispatch(updateAdditionLeftPadding(0));
    };
  }, []);

  const getSelectedTab = (tab: string | null): JSX.Element => {
    return getPageComponent(tab);
  };

  let browserPageTitle = "Villa | Project Summary";
  if (customer && isAdmin) {
    browserPageTitle = `Villa | ${Customer.getLastName(customer)} Project`;
  }

  let mainContent: any;

  if (isThankYouPage) {
    mainContent = (
      <>
        <Header navigation_override={<></>} />
        {getSelectedTab(pageId)}
      </>
    );
  } else if (portalType) {
    let content = null;
    switch (portalType) {
      case "Multi-unit banner":
        content = <B2BView />;
        mainContent = (
          <>
            <Header navigation_override={<></>} />
            {content}
            <br />
          </> 
        );
        break;
      case "D2C Primary Homes":
        mainContent = <PrimaryHomesSummary pageId={pageId} />;
        break;
      case "D2C ADUs":
      default:
        if (isStageInFullPortal) {
          content = <FPDashboardLayout pageId={pageId} />;
        } else if (isDepositPaid) {
          content = <HCPDashboardLayout pageId={pageId} />;
        } else {
          content = getSelectedTab(pageId);
        }
        mainContent = (
          <>
            <Header navigation_override={<></>} />
            {content}
            <br />
          </> 
        );
        break;
    }
    
  } else {
    mainContent = (
      <>
        <Header />
        {getSelectedTab(pageId)}
      </>
    );
  }

  return (
    <>
      <LoadingPanel show={!isSignedIn || !pid} showAuth={true} />
      <PortalLayout id={"portal-content-wrapper"}>
        <Helmet>
          <title>{browserPageTitle}</title>
          <meta name="description" content="Your Villa Dashboard" />
        </Helmet>
        <ErrorBoundary>{mainContent}</ErrorBoundary>
      </PortalLayout>
    </>
  );
};
